import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {PHONE_BOTTOM_BAR_HEIGHT} from 'src/components/mobile/mobileDesign.sx';

export const pageWrapperSx: SxProps<Theme> = {
    width: '100%',
    display: 'flex',
    /**
     * The shell may be narrower than what it holds.
     *
     * `#root` is a flex container (App.css), so this is a flex item - and a flex item's `min-width`
     * defaults to `auto`, which means "never narrower than my content". One wide thing anywhere
     * inside, like the pricing week at 1060px, therefore stretched the whole shell and every inner
     * scrollport with it: there was nothing left to scroll, and the overflow was simply clipped.
     * This is the top of that chain; without it the `min-width: 0` further down does nothing.
     */
    minWidth: 0,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(25 / 8),
    background: theme.palette.background.paper,
    [theme.breakpoints.down('md')]: {
        gap: 0,
    },
};
export const notificationsBoxSx: SxProps<Theme> = {
    position: 'absolute',
    top: theme.spacing(20 / 8),
    right: theme.spacing(50 / 8),
    // Below a desktop the app bar's own menu button occupies the right-hand corner, and the bell was
    // floating on top of it - two controls, one square, whichever the finger reached first. It steps
    // aside by exactly the width of that button and sits level with it, as the bar is drawn.
    [theme.breakpoints.down('md')]: {
        top: theme.spacing(10 / 8),
        right: theme.spacing(56 / 8),
    },
};
export const notificationsNumberSx: SxProps<Theme> = {
    position: 'absolute',
    top: theme.spacing(-5 / 8),
    right: theme.spacing(-5 / 8),
};
export const notificationsSx: SxProps<Theme> = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.neutral[100],
    borderRadius: '100%',
    width: theme.spacing(40 / 8),
    height: theme.spacing(40 / 8),
    '&:hover': {
        background: theme.palette.neutral[200],
        transition: 'background ease-in-out 0.4s',
        '& svg': {
            fill: theme.palette.neutral[500],
            transition: 'fill ease-in-out 0.4s',
        },
    },
    '& svg': {
        fill: theme.palette.neutral[400],
    },
};
export const pageContentSx: SxProps<Theme> = {
    padding: theme.spacing(50 / 8, 30 / 8),
    width: '100%',
    minWidth: 0,
    // Shell guard: the content column must never let a page push the document
    // wider than the viewport (which would slide content under the fixed drawer).
    // Inner scroll areas (chat messages, table scrollArea) keep their own scroll.
    overflowX: 'clip',
    position: 'relative',
    height: '100%',
    [theme.breakpoints.down('md')]: {
        // Two pixels at the sides below a desktop - not zero. The screen is 390px and the content
        // takes all but a hair of it; that hair is what keeps a card's edge and its shadow from
        // dying against the bezel, which is the difference between full width and flush.
        padding: theme.spacing(20 / 8, 2 / 8),
        // Room for the bottom bar, which is fixed and therefore out of the flow: without this the
        // last row of every list and any footer a page pins sit underneath it.
        paddingBottom: theme.spacing((20 + PHONE_BOTTOM_BAR_HEIGHT) / 8),
    },
};
export const preloaderWrapperSx: SxProps<Theme> = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    background: 'rgb(255,255,255,0.5)',
};
/**
 * How wide the page is allowed to get.
 *
 * It was MUI's `xl` container, which is 1536px — so on a 2560px monitor the product stopped a third
 * of the way across and left five hundred pixels of grey on each side, while the tables inside were
 * scrolling sideways. 1800px keeps growing well past a laptop and still stops before a table row
 * becomes a thing you read by turning your head; the long-form text inside (a tariff description, a
 * contract's terms) has its own measure and is unaffected either way.
 *
 * The cap is a desktop measure and only a desktop one: below `md` the column is the screen, and the
 * gutters around it are zero, so a card uses every pixel the phone has.
 *
 * One place, every page: every screen is rendered through this container.
 */
export const contentSx: SxProps<Theme> = {
    '&.MuiContainer-root': {
        paddingLeft: 0,
        paddingRight: 0,
        maxWidth: '1800px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
};

/** The welcome screen sets its own width and is not capped. */
export const fullWidthContentSx: SxProps<Theme> = {
    '&.MuiContainer-root': {
        paddingLeft: 0,
        paddingRight: 0,
    },
};
